import React, { useEffect, useMemo } from "react";

import { Formik } from "formik";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import PropTypes from "prop-types";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";

import { useSubmitSspUserMutation } from "../../../features/io/ioSspApiSlice";
import { setSspRegistrationData } from "../../../features/sspRegistrationSlice";
import useSignUpPasswordResponseHelper from "../../../hooks/useSignUpPasswordResponseHelper";
import { ReactComponent as IconPrivate18 } from "../../../icons/icon/18/a-icon-private-18.svg";
import {
  ReactComponent as IconDualToneSecurityPrimary48,
} from "../../../icons/icon/48/a-icon-dualtone-security-primary-48.svg";
import DataLayer from "../../../utils/dataLayer";
import ScrollToFieldError from "../../../utils/formik/scrollToFieldError";
import SspDataHelper from "../../accountSignup/sspDataHelper";
import IconButton from "../../button/iconButton";
import IconText from "../../iconText/iconText";
import PasswordInput from "../../passwordInput/passwordInput";
import RequirementList from "../../requirementList/requirementList";
import RegistrationDialogHeadline from "../registrationDialogHeadline";

const RegistrationDialogStepPassword = ({ next }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const sspRegistrationData = useSelector((state) => state.sspRegistration.sspRegistrationData);
  const requirements = SspDataHelper.getPasswordRequirements(intl);

  const [submitSspUser, submitSspUserResult] = useSubmitSspUserMutation();

  useSignUpPasswordResponseHelper(submitSspUserResult);

  useEffect(() => {
    if (submitSspUserResult.isSuccess && submitSspUserResult?.data?.identifier) {
      DataLayer.pushEvent("pp_registration_signup_password", { event_value: "success" });
      next();
    }

    if (submitSspUserResult.isError
      || (submitSspUserResult.isSuccess && !submitSspUserResult?.data?.identifier)) {
      DataLayer.pushEvent("pp_registration_signup_password", { event_value: "fail" });
    }
  }, [submitSspUserResult]);

  return (
    <>
      <IconDualToneSecurityPrimary48 className="mb-4" />
      <RegistrationDialogHeadline
        headline={intl.formatMessage({ id: "registration.password.title" })}
      />
      <Formik
        initialValues={{
          confirmPassword: "",
          password: "",
        }}
        validationSchema={
          useMemo(() => SspDataHelper.getPasswordValidationSchema(intl, requirements), [])
        }
        validateOnMount
        onSubmit={(values, formikBag) => {
          formikBag.setSubmitting(true);

          dispatch(
            setSspRegistrationData({
              password: values.password,
            }),
          );

          submitSspUser(
            SspDataHelper.preparePayloadForSubmitUser({
              ...sspRegistrationData,
              password: values.password,
            }),
          );
        }}
      >
        {({
          handleSubmit,
          values,
          touched,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6} lg={12}>
                <PasswordInput
                  label={intl.formatMessage({ id: "registration.password.label" })}
                  name="password"
                  required
                />
              </Col>
              <Col md={6} lg={12}>
                <PasswordInput
                  label={intl.formatMessage({ id: "registration.password_repeat.label" })}
                  name="confirmPassword"
                  required
                />
              </Col>
            </Row>
            <RequirementList
              password={values.password}
              requirements={requirements}
              touched={touched.password}
            />

            <div className="registration-dialog__ssl-encrypted-container">
              <IconButton
                type="submit"
                disabled={submitSspUserResult.isLoading}
                loading={submitSspUserResult.isLoading}
                className="mt-0"
              >
                <FormattedMessage id="registration.set_password.label" />
              </IconButton>
              <IconText
                icon={<IconPrivate18 />}
                variant="grey-dark"
                text={intl.formatMessage({ id: "registration.ssl_encrypted" })}
              />
            </div>

            <ScrollToFieldError />

            <div className="footnote--required">
              <span className="required-sign" />
              <FormattedMessage id="registration.required_fields" />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

RegistrationDialogStepPassword.stepName = "registration_signup_password";

RegistrationDialogStepPassword.propTypes = {
  next: PropTypes.func.isRequired,
};

export default RegistrationDialogStepPassword;
